import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue'

import type { InsightColumnDefinition, InsightElement } from '../models/insight.types'
import {
  elementToTable,
  mapInsightElement,
  type InsightElementMapping,
} from '../service/insightElementMapper'

import { useMetricCatalog } from '@/features/reports'

export interface PresentedInsightElement {
  key: string
  element: InsightElement
  mapping: InsightElementMapping
}

export interface UseInsightElements {
  items: ComputedRef<readonly PresentedInsightElement[]>
}

/** Bridges persisted InsightElement DTOs onto module 08's catalog-aware presenter inputs. */
export function useInsightElements(
  elements: MaybeRefOrGetter<readonly InsightElement[]>,
  configColumns: MaybeRefOrGetter<readonly InsightColumnDefinition[]> = [],
): UseInsightElements {
  const catalog = useMetricCatalog()

  const items = computed(() => {
    const source = toValue(elements)
    const columns = toValue(configColumns)
    return source.map((element, index) => ({
      key: `${element.order}:${element.tab_key ?? ''}:${element.element_type}:${index}`,
      element,
      mapping: mapInsightElement(
        element,
        {
          getColumn: catalog.getColumn,
          metricLabel: catalog.metricLabel,
          configColumns: columns,
        },
        element.element_type === 'funnel'
          ? source
              .filter(
                (candidate) =>
                  candidate.element_type === 'table' && candidate.tab_key === element.tab_key,
              )
              .flatMap(
                (candidate) => elementToTable(candidate, { configColumns: columns })?.rows ?? [],
              )
          : undefined,
      ),
    }))
  })

  return { items }
}
